import TIM from 'tim-js-sdk'

import {
  postInteractionDirectStartForClient,
  getInteractionUserSig,
  verifyGroup,
  getInteractionControl,
  endInteractionByClient
} from '@/api/presentation.js'
import logger from '@/base/logger.js'
import adapter from './adapter'

export function extendTim () {
  adapter.ENV = 'web'
  adapter.install(TIM)
  adapter.quitPresentation = function quitPresentation (isEndInvalidateRoom = true) {
    const {pbUuid, salesUuid, clientUuid, roomId} = this.pageContext
    if (!roomId || !this.dashboard.isInGroup) {
      return
    }
    this.stopAudioChat()
    if (isEndInvalidateRoom) {
      endInteractionByClient({
        pbUuid,
        salesUuid,
        clientUuid,
        groupId: String(roomId)
      })
    }
    this.quitGroup()
    Object.assign(this.dashboard, {
      isLogin: false,
      isInGroup: false
    })
    this.pageContext.roomId = null
  }
  adapter.joinPresentation = async function () {
    const { pbUuid, roomId, salesUuid, clientUuid } = this.pageContext
    const { groupId } = await verifyGroup({
      clientUuid,
      salesUuid,
      pbUuid,
      groupId: roomId
    })
    if (groupId != roomId) {
      logger.warn('[we-tim]', '接口返回新房间号：', groupId, '链接房间号：', roomId)
    }
    await this.joinGroup(String(groupId))
    this.pageContext.roomId = parseInt(groupId, 10)
    this.dashboard.isInGroup = true
    return this
  },
  adapter.requestPresenterControl = function requestPresenterControl () {
    const { clientUuid, salesUuid, pbUuid, roomId } = this.pageContext
    return getInteractionControl({
      agentSite: false,
      clientUuid,
      salesUuid,
      pbUuid,
      groupId: roomId
    })
  }

  adapter.trtcClient = null
  adapter.trtcLocalStream = null
  adapter.startAudioChat = async function startAudioChat (messageHandler = function () {}) {
    const TRTC = await import(/* webpackChunkName: "trtc-js-sdk" */'trtc-js-sdk').then(e => e.default)
    const { dashboard, pageContext } = this
    if (dashboard.isAudioConnected) {
      logger.warn('重复开启音频')
      return true
    }

    const { userId, sdkAppId, userSig, roomId } = pageContext
    // 创建 Client 对象
    const client = TRTC.createClient({
      mode: 'rtc',
      sdkAppId,
      userId,
      userSig
    })

    // 订阅远端音视频流。————请在 client.join() 进房前注册该事件
    client.on('stream-added', event => {
      const remoteStream = event.stream
      console.log('远端流增加: ' + remoteStream.getId())
      client.subscribe(remoteStream)
    })
    client.on('stream-subscribed', event => {
      const remoteStream = event.stream
      console.log('远端流订阅成功：' + remoteStream.getId())
      // 如果您遇到播放失败的问题，需要使用 remoteStream 监听 error 并处理 0x4043 浏览器限制自动播放的问题
      remoteStream.on('error', error => {
        const errorCode = error.getCode()
        if (errorCode === 0x4043) {
          // PLAY_NOT_ALLOWED, 引导用户手势操作并调用 stream.resume 恢复音视频播放，
          // 在点击回调里调用 remoteStream.resume() 进行恢复播放。
        }
      })
      remoteStream.play('remoteStreamContainer')
    })

    // 进入音视频通话房间
    try {
      await client.join({
        roomId: Number(roomId)
      }).then(() => {
        messageHandler({
          msg: '开启实时语音成功'
        })
      })
    } catch (error) {
      messageHandler({
        msg: `开启实时语音失败${error}`
      })
    }

    // 创建、初始化、发布本地音视频流
    const localStream = TRTC.createStream({
      userId,
      audio: true,
      video: false
    })
    try {
      await localStream.initialize()
      console.log('初始化本地流成功')
      localStream.play('localStreamContainer')
    } catch (error) {
      console.error('初始化本地流失败 ' + error)
    }
    try {
      await client.publish(localStream)
      console.log('本地流发布成功')
    } catch (error) {
      console.error('本地流发布失败 ' + error)
    }

    dashboard.isAudioConnected = true
    this.trtcClient = client
    this.trtcLocalStream = localStream
  }
  adapter.stopAudioChat = async function stopAudioChat () {
    const { dashboard, trtcClient: client } = this
    if (!dashboard.isAudioConnected) {
      logger.warn('音频未初始化成功无需销毁。或重复销毁音频')
      return
    }

    // 退出音视频通话房间
    await client.leave()
    // 退房成功，如果没有调用 client.destroy()，可再次调用 client.join 重新进房开启新的通话
    // 调用 destroy() 结束当前 client 的生命周期
    client.destroy()
    dashboard.isAudioConnected = false
    client.on('client-banned', error => {
      dashboard.isAudioConnected = false
      console.error(`client-banned observed, reason:${error.reason}, message:${error.message}`)
      // error.reason 有以下几种情况
      // 'kick' 由于相同 userId 进房导致先进入的用户被移出房间
      // 'banned' 被管理员移出房间
      // 'room-disband' 管理员解散了房间
    })
    return this
  }
  adapter.muteLocal = function muteLocal (isOn = false) {
    if (isOn) {
      this.trtcLocalStream.muteAudio()
    } else {
      this.trtcLocalStream.unmuteAudio()
    }
    return this
  }
}

export async function initTim ({ avatar, nick, token, appId, pbUuid, salesUuid, roomId, tobAppId, interactionType }) {
  extendTim()
  const tim = adapter
  tim.dashboard = {
    isLogin: false,
    isInGroup: false,
    isAudioConnected: false,
    isSdkReady: false
  }

  const { clientUuid } = await postInteractionDirectStartForClient({
    token,
    appId,
    salesUuid,
    interactionUuid: pbUuid,
    interactionType: interactionType || 4,
    tobAppId,
    groupId: roomId
  })

  if (!clientUuid) {
    console.error('[we-tim]', 'CLIENT_UUID_NULL')
    throw new Error('CLIENT_UUID_NULL')
  }

  const { userSig, sdkAppId, userId } = (await getInteractionUserSig(clientUuid)) || {}
  tim.create(sdkAppId)
  await tim.login(userId, userSig).then(imResponse => {
    console.log(imResponse.data) // 登录成功
    if (imResponse.data.repeatLogin === true) {
      // 标识帐号已登录，本次登录操作为重复登录。v2.5.1 起支持
      console.log(imResponse.data.errorInfo)
    }
  }).catch(function (imError) {
    console.warn('login error:', imError) // 登录失败的相关信息
  })
  tim.dashboard.isLogin = true

  if (!roomId) {
    throw new Error('INVALID_ROOMID')
  }
  tim.pageContext = { pbUuid, sdkAppId, userId, userSig, avatar, nick, roomId, salesUuid, clientUuid }

  const sdkReadyPromise = new Promise(resolve => {
    tim.on(tim.EVENTS.onSdkReady, resolve)
  })
  await sdkReadyPromise
  tim.dashboard.isSdkReady = true
  await tim.updateMyProfile({ avatar, nick })
  await tim.joinPresentation()
  return tim
}

window.TimAdapter = adapter
